import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { API_CONFIG } from '../../utils/constants'
import { buildApiUrl } from '../../config/api'
import { saveReturnTo } from '../../utils/returnTo'
import { redirectTo } from '../../utils/browserNavigation'

/** Default injetado quando o backend não mandou mensagem — quem compara
 * (ex.: errMsg) importa daqui em vez de duplicar a string. */
export const GENERIC_API_ERROR = 'Erro inesperado'

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: buildApiUrl(''),
    timeout: API_CONFIG.TIMEOUT,
    headers: { 'Content-Type': 'application/json' },
  })

  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token')
      if (token) config.headers.Authorization = `Bearer ${token}`

      // FormData precisa do boundary automático
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type']
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<any>) => {
      // 401: força logout
      if (error.response?.status === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        // Hard navigation perde o state do router — guarda a rota atual pra
        // voltar pra ela após o login.
        saveReturnTo(window.location.pathname + window.location.search)
        redirectTo('/login')
        return Promise.reject(error)
      }

      let data = error.response?.data as any
      const status = error.response?.status

      // Em requests com responseType:'blob' (downloads), o corpo de erro do
      // Nest chega como Blob — sem desempacotar, qualquer falha viraria
      // "Erro inesperado" e o usuário não saberia o motivo real.
      if (typeof data?.text === 'function') {
        try {
          data = JSON.parse(await data.text())
        } catch {
          data = undefined // corpo não-JSON (ex.: HTML de proxy)
        }
      }

      let message = GENERIC_API_ERROR

      if (data) {
        if (Array.isArray(data.message)) message = data.message.join('\n')
        else if (typeof data.message === 'string') message = data.message
        else if (typeof data.error === 'string') message = data.error
        else if (typeof data.detail === 'string') message = data.detail
      } else if (error.message) {
        message = error.message
      }

      // Array do class-validator vira string única também dentro do corpo, para
      // quem lê `err.response.data.message` direto não renderizar um array.
      if (data && Array.isArray(data.message)) data.message = message

      // Rejeita com um Error "normal" com a message correta, PRESERVANDO
      // `response`: quase uma centena de call sites do front lê
      // `err.response.data.message` / `err.response.status` (409 do vínculo
      // SIGRA, 404 de preferências, corpo Blob dos downloads), e o cliente
      // público (publicQuotationService) já entrega o erro nesse formato.
      const wrapped = new Error(message) as Error & {
        status?: number
        data?: any
        response?: AxiosError['response']
      }
      wrapped.status = status
      wrapped.data = data
      wrapped.response = error.response

      return Promise.reject(wrapped)
    }
  )

  return client
}

export const apiClient = createApiClient()